import React, { useMemo, useState } from 'react';
import {
  BOOKING_BARRIERS,
  CALL_INTENTS,
  COMPETITOR_INTEL,
  ESCALATION_CAUSES,
  KNOWLEDGE_GAPS,
  PATIENT_FAQS,
  PATIENT_SOURCES,
} from '../../reports/mockData';
import {
  CATEGORY_KIND,
  OUTCOME_KIND,
  OUTCOME_LABEL,
  PILL_KIND,
  RPT_TOKENS,
  STATUS_KIND,
} from '../../reports/config';
import type { ReportsTimeRange } from '../../reports/types';
import { Icon } from '../Icon/Icon';
import {
  AddToBrainButton,
  InsightBanner,
  KindPill,
  ReportPageHeader,
  ReportTable,
  SearchBar,
  Td,
  Th,
  TrendCell,
} from './ReportShared';

export interface ReportPageProps {
  timeRange: ReportsTimeRange;
  onTimeRangeChange: (value: ReportsTimeRange) => void;
}

export function BookingBarriersPage({ timeRange, onTimeRangeChange }: ReportPageProps) {
  return (
    <div className="rpt-fade-up rpt-d-0">
      <ReportPageHeader
        eyebrow="Reports · acquisition"
        title="Booking"
        italic="barriers"
        description="Patients who called but didn't book — and why. Use this to spot where you're losing patients in the funnel."
        metric={BOOKING_BARRIERS.total}
        metricLabel={`Missed opportunities · ${BOOKING_BARRIERS.windowLabel}`}
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
      />
      <div className="mb-6 grid grid-cols-5 gap-3">
        {BOOKING_BARRIERS.buckets.map((b) => (
          <div
            key={b.id}
            className="rpt-kpi-card rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-3.5 pb-4 shadow-[var(--sh-1)]"
          >
            <div className="rpt-eyebrow mb-2 text-[10px]">{b.label}</div>
            <div className="flex items-baseline gap-2">
              <span className="rpt-tab-num text-[30px] font-semibold leading-none tracking-[-0.03em] text-[var(--ink-0)]">
                {b.count}
              </span>
              <span className="rpt-mono text-[12px] font-semibold" style={{ color: PILL_KIND[b.kind].color }}>
                {b.pct}%
              </span>
            </div>
          </div>
        ))}
      </div>
      <SearchBar placeholder="Search by patient name or insurance…" />
      <ReportTable>
        <thead>
          <tr>
            <Th>Patient</Th>
            <Th>Date</Th>
            <Th>Reason</Th>
            <Th>Details</Th>
            <Th>Insurance</Th>
          </tr>
        </thead>
        <tbody>
          {BOOKING_BARRIERS.rows.map((r) => {
            const bucket = BOOKING_BARRIERS.buckets.find((b) => b.id === r.reasonId)!;
            return (
              <tr key={r.id} className="rpt-row cursor-pointer">
                <Td color="var(--ink-0)" weight={500}>
                  {r.patient}
                </Td>
                <Td color={RPT_TOKENS.ink3}>{r.date}</Td>
                <Td>
                  <KindPill kind={bucket.kind} label={bucket.label} />
                </Td>
                <Td maxWidth={320}>{r.details}</Td>
                <Td>{r.insurance}</Td>
              </tr>
            );
          })}
        </tbody>
      </ReportTable>
    </div>
  );
}

export function CompetitorIntelPage({ timeRange, onTimeRangeChange }: ReportPageProps) {
  return (
    <div className="rpt-fade-up rpt-d-0">
      <ReportPageHeader
        eyebrow="Reports · market"
        title="Competitor"
        italic="intel"
        description="What patients are saying about other dental practices during calls. The phrases they use are the offers you're being compared against."
        metric={COMPETITOR_INTEL.total}
        metricLabel={`Total mentions · ${COMPETITOR_INTEL.windowLabel}`}
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
      />
      <SearchBar placeholder="Search competitors…" />
      <div className="flex flex-col gap-3">
        {COMPETITOR_INTEL.competitors.map((c, i) => {
          const trendValue = parseInt(c.trend, 10);
          const isUp = trendValue > 0;
          const isFlat = trendValue === 0 || Number.isNaN(trendValue);
          return (
            <div
              key={c.id}
              className="rpt-fade-up grid items-start gap-[22px] rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)] p-5 px-[22px] shadow-[var(--sh-1)]"
              style={{ gridTemplateColumns: '1fr 110px', animationDelay: `${(i + 1) * 60}ms` }}
            >
              <div className="min-w-0">
                <div className="mb-1 flex flex-wrap items-center gap-2.5">
                  <h3 className="m-0 text-[17px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                    {c.name}
                  </h3>
                  <span
                    className="rpt-mono text-[12px] font-semibold"
                    style={{
                      color: isFlat ? RPT_TOKENS.ink4 : isUp ? RPT_TOKENS.greenDeep : RPT_TOKENS.negative,
                    }}
                  >
                    {isFlat ? '—' : isUp ? '▲' : '▼'} {c.trend}
                  </span>
                  <span className="text-[11.5px] text-[var(--ink-2)] opacity-70">vs last week</span>
                </div>
                <div className="mb-3.5 text-[12px] tracking-[-0.005em] text-[var(--ink-2)] opacity-70">
                  Last mentioned: {c.lastMentioned}
                </div>
                <div className="mb-3 rounded-[10px] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3.5 py-3">
                  <div className="rpt-eyebrow mb-1.5 text-[9.5px]">Most common patient quote</div>
                  <div className="text-[14px] font-medium leading-normal tracking-[-0.005em] text-[var(--ink-0)]">
                    &ldquo;{c.topQuote}&rdquo;
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-1.5">
                    {c.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-md bg-[var(--surface-mute)] px-2 py-[3px] text-[11px] font-medium text-[var(--ink-2)]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent text-[12.5px] font-semibold tracking-[-0.005em]"
                    style={{ color: RPT_TOKENS.greenDeep }}
                  >
                    View {c.callsCount} patient calls <Icon kind="right" size={11} stroke={2} />
                  </button>
                </div>
              </div>
              <div
                className="rounded-[11px] border px-3 py-4 text-center"
                style={{ background: RPT_TOKENS.brassTint, borderColor: 'rgba(168, 132, 92, 0.22)' }}
              >
                <div className="rpt-tab-num mb-1 text-[28px] font-semibold leading-none tracking-[-0.025em]" style={{ color: RPT_TOKENS.brassDeep }}>
                  {c.mentions}
                </div>
                <div className="text-[11px] tracking-[-0.005em]" style={{ color: RPT_TOKENS.brassDeep }}>
                  mentions
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PatientSourcesPage({ timeRange, onTimeRangeChange }: ReportPageProps) {
  return (
    <div className="rpt-fade-up rpt-d-0">
      <ReportPageHeader
        eyebrow="Reports · acquisition"
        title="Patient"
        italic="sources"
        description="How new patients are finding your practice. The number on the right is your booking rate per channel — that's what should drive marketing spend."
        metric={PATIENT_SOURCES.total}
        metricLabel={`Total new patient calls · ${PATIENT_SOURCES.windowLabel}`}
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
      />
      <div className="mb-[22px] grid grid-cols-3 gap-3">
        {[
          { label: 'Avg. booking rate', value: `${PATIENT_SOURCES.avgBookingRate}%`, sub: 'New patient conversion' },
          { label: 'Top source', value: PATIENT_SOURCES.topSource.name, sub: `${PATIENT_SOURCES.topSource.calls} calls this week` },
          { label: 'Best conversion', value: PATIENT_SOURCES.bestConversion.name, sub: `${PATIENT_SOURCES.bestConversion.rate}% booking rate` },
        ].map((s) => (
          <div key={s.label} className="rpt-kpi-card rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-5 py-4 shadow-[var(--sh-1)]">
            <div className="rpt-eyebrow mb-2 text-[10px]">{s.label}</div>
            <div className="mb-1 text-[22px] font-semibold leading-[1.15] tracking-[-0.025em] text-[var(--ink-0)]">
              {s.value}
            </div>
            <div className="text-[11.5px] tracking-[-0.005em] text-[var(--ink-2)]">{s.sub}</div>
          </div>
        ))}
      </div>
      <ReportTable>
        <thead>
          <tr>
            <Th>Source</Th>
            <Th align="right">Calls</Th>
            <Th align="right">Booked</Th>
            <Th align="right">Booking rate</Th>
            <Th align="right">Trend</Th>
          </tr>
        </thead>
        <tbody>
          {PATIENT_SOURCES.rows.map((r) => {
            const rateColor =
              r.rate >= 75 ? RPT_TOKENS.greenDeep : r.rate >= 60 ? RPT_TOKENS.warning : RPT_TOKENS.negative;
            return (
              <tr key={r.id} className="rpt-row cursor-pointer">
                <Td color="var(--ink-0)" weight={500}>
                  {r.label}
                </Td>
                <Td align="right" mono color="var(--ink-0)" weight={500}>
                  {r.calls}
                </Td>
                <Td align="right" mono color="var(--ink-0)" weight={500}>
                  {r.booked}
                </Td>
                <Td align="right" mono color={rateColor} weight={600}>
                  {r.rate}%
                </Td>
                <Td align="right">
                  <TrendCell value={r.trend} />
                </Td>
              </tr>
            );
          })}
        </tbody>
      </ReportTable>
      <div className="mt-3.5 pl-1 text-[12px] text-[var(--ink-2)]">
        {PATIENT_SOURCES.unattributedCount} of {PATIENT_SOURCES.total} new-patient calls had no source captured.
      </div>
    </div>
  );
}

export function CallIntentsPage({ timeRange, onTimeRangeChange }: ReportPageProps) {
  const maxShare = Math.max(...CALL_INTENTS.rows.map((r) => r.share));
  return (
    <div className="rpt-fade-up rpt-d-0">
      <ReportPageHeader
        eyebrow="Reports · volume"
        title="Call"
        italic="intents"
        description="What patients are calling about, broken down by topic. The bar shows share of total volume."
        metric={CALL_INTENTS.total.toLocaleString()}
        metricLabel={`Total calls · ${CALL_INTENTS.windowLabel}`}
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
      />
      <InsightBanner>
        <strong className="font-semibold text-[var(--ink-0)]">Billing queries are up 15%</strong> — consider
        adding more billing FAQs to the Knowledge Base or reviewing your billing statements for clarity.
      </InsightBanner>
      <ReportTable>
        <thead>
          <tr>
            <Th>Intent</Th>
            <Th align="right">Calls</Th>
            <Th>Share</Th>
            <Th align="right">AI resolved</Th>
            <Th align="right">Booked</Th>
            <Th>Top outcome</Th>
            <Th align="right">Trend</Th>
          </tr>
        </thead>
        <tbody>
          {CALL_INTENTS.rows.map((r) => {
            const resColor =
              r.resolved >= 90 ? RPT_TOKENS.greenDeep : r.resolved >= 75 ? RPT_TOKENS.warning : RPT_TOKENS.negative;
            return (
              <tr key={r.id} className="rpt-row cursor-pointer">
                <Td color="var(--ink-0)" weight={500}>
                  {r.label}
                </Td>
                <Td align="right" mono color="var(--ink-0)" weight={500}>
                  {r.calls}
                </Td>
                <Td>
                  <div className="flex items-center gap-2.5">
                    <div className="h-[5px] max-w-[120px] flex-1 overflow-hidden rounded-full bg-[var(--surface-mute)]">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${(r.share / maxShare) * 100}%`, background: RPT_TOKENS.green }}
                      />
                    </div>
                    <span className="rpt-mono min-w-8 text-[12px] text-[var(--ink-1)]">{r.share}%</span>
                  </div>
                </Td>
                <Td align="right" mono color={resColor} weight={600}>
                  {r.resolved}%
                </Td>
                <Td align="right" mono color="var(--ink-1)">
                  {r.booked != null ? r.booked : '—'}
                </Td>
                <Td>
                  <KindPill kind={OUTCOME_KIND[r.topOutcome] ?? 'neutral'} label={OUTCOME_LABEL[r.topOutcome] ?? r.topOutcome} />
                </Td>
                <Td align="right">
                  <TrendCell value={r.trend} />
                </Td>
              </tr>
            );
          })}
        </tbody>
      </ReportTable>
    </div>
  );
}

export function PatientFAQsPage({ timeRange, onTimeRangeChange }: ReportPageProps) {
  const [activeCategory, setActiveCategory] = useState('All');
  const filtered = useMemo(
    () =>
      activeCategory === 'All'
        ? PATIENT_FAQS.rows
        : PATIENT_FAQS.rows.filter((r) => r.category === activeCategory),
    [activeCategory],
  );

  return (
    <div className="rpt-fade-up rpt-d-0">
      <ReportPageHeader
        eyebrow="Reports · knowledge"
        title="Patient"
        italic="FAQs"
        description="The most frequently asked questions Elva has fielded — across calls, SMS, email, and chat. Use this to find gaps in your knowledge base."
        metric={PATIENT_FAQS.unanswered}
        metricLabel="Unanswered questions"
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
      />
      <div className="mb-[22px] flex flex-wrap gap-1.5">
        {PATIENT_FAQS.categories.map((cat) => {
          const isActive = activeCategory === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              className="inline-flex cursor-pointer items-center rounded-full px-3.5 py-1.5 text-[12.5px] font-medium tracking-[-0.005em]"
              style={{
                background: isActive ? RPT_TOKENS.greenWash : 'var(--cream-1)',
                color: isActive ? RPT_TOKENS.greenDeep : 'var(--ink-1)',
                border: `1px solid ${isActive ? RPT_TOKENS.greenSoft : 'var(--cream-2)'}`,
                fontWeight: isActive ? 600 : 500,
                boxShadow: isActive ? 'none' : 'var(--sh-1)',
              }}
            >
              {cat}
            </button>
          );
        })}
      </div>
      <ReportTable>
        <thead>
          <tr>
            <Th width={50}>#</Th>
            <Th>Question</Th>
            <Th>Category</Th>
            <Th align="right">Frequency</Th>
            <Th>Status</Th>
            <Th>Last asked</Th>
            <Th width={140} />
          </tr>
        </thead>
        <tbody>
          {filtered.map((r) => (
            <tr key={r.id} className="rpt-row cursor-pointer">
              <Td mono color={RPT_TOKENS.ink4}>
                {String(r.id).padStart(2, '0')}
              </Td>
              <Td color="var(--ink-0)" weight={500} maxWidth={420}>
                {r.question}
              </Td>
              <Td>
                <KindPill kind={CATEGORY_KIND[r.category] ?? 'neutral'} label={r.category} />
              </Td>
              <Td align="right" mono color="var(--ink-0)" weight={500}>
                {r.frequency}×
              </Td>
              <Td>
                <KindPill
                  kind={STATUS_KIND[r.status] ?? 'neutral'}
                  label={r.status === 'answered' ? 'Answered' : r.status === 'unanswered' ? 'Unanswered' : 'Added'}
                />
              </Td>
              <Td color={RPT_TOKENS.ink3}>{r.lastAsked}</Td>
              <Td>{r.status === 'unanswered' && <AddToBrainButton />}</Td>
            </tr>
          ))}
        </tbody>
      </ReportTable>
    </div>
  );
}

export function KnowledgeGapsPage({ timeRange, onTimeRangeChange }: ReportPageProps) {
  return (
    <div className="rpt-fade-up rpt-d-0">
      <ReportPageHeader
        eyebrow="Reports · knowledge"
        title="Knowledge"
        italic="gaps"
        description="Questions Elva couldn't answer because the information wasn't in her Knowledge base. The higher the frequency, the more it costs you."
        metric={KNOWLEDGE_GAPS.pending}
        metricLabel="Pending questions"
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
      />
      <SearchBar placeholder="Search questions…" />
      <ReportTable>
        <thead>
          <tr>
            <Th>Question</Th>
            <Th align="right">Frequency</Th>
            <Th>Category</Th>
            <Th>Last asked</Th>
            <Th>Status</Th>
            <Th width={140} />
          </tr>
        </thead>
        <tbody>
          {KNOWLEDGE_GAPS.rows.map((r) => {
            const freqColor =
              r.frequency >= 10 ? RPT_TOKENS.negative : r.frequency >= 5 ? RPT_TOKENS.warning : 'var(--ink-1)';
            return (
              <tr key={r.id} className="rpt-row cursor-pointer">
                <Td color="var(--ink-0)" weight={500} maxWidth={420}>
                  &ldquo;{r.question}&rdquo;
                </Td>
                <Td align="right" mono color={freqColor} weight={600}>
                  {r.frequency}×
                </Td>
                <Td>
                  <KindPill kind={CATEGORY_KIND[r.category] ?? 'neutral'} label={r.category} />
                </Td>
                <Td color={RPT_TOKENS.ink3}>{r.lastAsked}</Td>
                <Td>
                  <KindPill kind={STATUS_KIND[r.status] ?? 'neutral'} label={r.status === 'pending' ? 'Pending' : 'Added'} />
                </Td>
                <Td>{r.status === 'pending' && <AddToBrainButton />}</Td>
              </tr>
            );
          })}
        </tbody>
      </ReportTable>
    </div>
  );
}

export function EscalationCausesPage({ timeRange, onTimeRangeChange }: ReportPageProps) {
  return (
    <div className="rpt-fade-up rpt-d-0">
      <ReportPageHeader
        eyebrow="Reports · operations"
        title="Escalation"
        italic="causes"
        description="Why calls are being transferred to your staff instead of handled by Elva. Each card includes a concrete suggestion for reducing the volume."
        metric={ESCALATION_CAUSES.total}
        metricLabel={`Total escalations · ${ESCALATION_CAUSES.windowLabel}`}
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
      />
      <InsightBanner>
        <strong className="font-semibold text-[var(--ink-0)]">70% of staff interruptions</strong> are driven by
        billing and insurance complexities. Adding more billing FAQs could reduce escalations by an estimated 25%.
      </InsightBanner>
      <div className="flex flex-col gap-3.5">
        {ESCALATION_CAUSES.rows.map((r, i) => {
          const railColor = PILL_KIND[r.railColor].color;
          const resColor =
            r.resolutionRate >= 85 ? RPT_TOKENS.greenDeep : r.resolutionRate >= 70 ? RPT_TOKENS.warning : RPT_TOKENS.negative;
          return (
            <div
              key={r.id}
              className="rpt-fade-up grid items-start gap-6 rounded-r-xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-[26px] py-[22px] shadow-[var(--sh-1)]"
              style={{ gridTemplateColumns: '1fr 120px', borderLeft: `3px solid ${railColor}`, animationDelay: `${(i + 1) * 70}ms` }}
            >
              <div className="min-w-0">
                <div className="mb-[18px] flex flex-wrap items-center gap-2.5">
                  <h3 className="m-0 text-[18px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">{r.cause}</h3>
                  <span className="rpt-mono text-[12px] font-medium text-[var(--ink-2)] opacity-70">
                    {r.pct}% of escalations
                  </span>
                </div>
                <div className="mb-4 grid grid-cols-3 gap-5">
                  <div>
                    <div className="rpt-eyebrow mb-1 text-[9.5px]">Avg duration</div>
                    <div className="rpt-tab-num text-[22px] font-semibold tracking-[-0.025em] text-[var(--ink-0)]">
                      {r.avgDuration}
                    </div>
                  </div>
                  <div>
                    <div className="rpt-eyebrow mb-1 text-[9.5px]">Resolution rate</div>
                    <div className="rpt-tab-num text-[22px] font-semibold tracking-[-0.025em]" style={{ color: resColor }}>
                      {r.resolutionRate}%
                    </div>
                  </div>
                  <div>
                    <div className="rpt-eyebrow mb-1 text-[9.5px]">Top action taken</div>
                    <div className="text-[13.5px] font-medium leading-[1.4] tracking-[-0.005em] text-[var(--ink-0)]">
                      {r.topAction}
                    </div>
                  </div>
                </div>
                <div
                  className="flex items-start gap-2 rounded-[9px] px-3.5 py-2.5"
                  style={{ background: RPT_TOKENS.brassTint, border: '1px solid rgba(168, 132, 92, 0.22)' }}
                >
                  <Icon kind="sparkle" size={12} color={RPT_TOKENS.brassDeep} stroke={2} className="mt-0.5 shrink-0" />
                  <span className="text-[12.5px] leading-normal tracking-[-0.005em] text-[var(--ink-1)]">
                    <strong className="font-semibold text-[var(--ink-0)]">Suggestion:</strong> {r.suggestion}
                  </span>
                </div>
              </div>
              <div
                className="rounded-[11px] border border-[var(--cream-2)] px-3 py-[18px] text-center"
                style={{ background: PILL_KIND[r.railColor].bg }}
              >
                <div className="rpt-tab-num mb-1 text-[30px] font-semibold leading-none tracking-[-0.025em]" style={{ color: railColor }}>
                  {r.count}
                </div>
                <div className="text-[11px] opacity-85" style={{ color: railColor }}>
                  calls
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
